import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft } from "lucide-react";
import SEO, { buildBreadcrumb, toAbsolute } from "../components/SEO";

export function BlogListPage() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)); }, []);
  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
      <SEO
        title="Blog · Mimes Gourmet"
        description="Recetas, maridajes y notas de obrador desde Graus (Pirineo aragonés). Descubre cómo cocinamos y disfrutamos nuestras mermeladas artesanas."
        path="/blog"
        image="/products/frutos-rojos.webp"
        type="website"
      />
      <p className="eyebrow mb-3">Blog · recetas y notas</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-[color:var(--brand-primary)] font-light">Nuestro <span className="italic text-[color:var(--brand-terracotta)]">diario</span></h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
        {posts.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group">
            <div className="aspect-[4/3] overflow-hidden rounded-md bg-[color:var(--brand-bg-alt)]">
              <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <p className="eyebrow mt-4">{p.published_at}</p>
            <h3 className="font-serif text-xl mt-2 text-[color:var(--brand-primary)] group-hover:text-[color:var(--brand-terracotta)]">{p.title}</h3>
            <p className="text-sm mt-2 text-[color:var(--brand-neutral)]">{p.excerpt}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data));
  }, [slug]);
  if (!post) return <div className="p-16 text-center">Cargando…</div>;
  return (
    <article className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
      <SEO
        title={`${post.title} · Mimes Gourmet`}
        description={post.excerpt || post.title}
        path={`/blog/${post.slug}`}
        image={post.image}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: [toAbsolute(post.image)],
            datePublished: post.published_at,
            author: { "@type": "Organization", name: "Mimes Gourmet" },
            publisher: {
              "@type": "Organization",
              name: "Mimes Gourmet",
              logo: { "@type": "ImageObject", url: toAbsolute("/favicon.svg") },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://mimesgourmet.es/blog/${post.slug}`,
            },
          },
          buildBreadcrumb([
            { name: "Inicio", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
        ]}
      />
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-[color:var(--brand-neutral)] hover:text-[color:var(--brand-primary)] mb-8"><ArrowLeft className="w-3.5 h-3.5" /> Volver al blog</Link>
      <p className="eyebrow">{post.published_at} · {post.author}</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-3 text-[color:var(--brand-primary)] font-light leading-tight">{post.title}</h1>
      <div className="aspect-video mt-8 overflow-hidden rounded-md"><img src={post.image} alt={post.title} className="w-full h-full object-cover" /></div>
      <div className="mt-8 blog-content prose prose-lg max-w-none text-[color:var(--brand-neutral)] leading-relaxed" dangerouslySetInnerHTML={{ __html: post.content }} />
    </article>
  );
}
